<template>
  <div class="talentPool-status-container">
    <div class="title">
      <span class="title-left">{{ $t(`${i18nPrefix}.btn.${title}`) }}</span>
      <el-tooltip
        effect="light"
        popper-class="light-tooltip-popper"
        :content="groupName"
      >
        <div class="title-group-name">{{ groupName }}</div>
      </el-tooltip>
      <router-link class="to-detail" :to="`/v-admin/talent-pool/${groupID}`">
        {{ $t('admin.talent.label.groupFilterDetail') }}<i class="el-icon-arrow-right"/>
      </router-link>
    </div>
    <div class="chart">
      <div class="chart-left" ref="chart"></div>
      <div class="chart-number">{{ number | formatBigNumber }}</div>
      <ul class="chart-list">
        <li v-for="(item, index) in chartList" class="chart-li" :key="index">
          <div>
            <span class="color" :style="'background:' + item.color"></span>
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="$t(`admin.crm.label.talent.candidateStatus.${item.name.toLowerCase()}`)"
            >
              <span class="name">{{ item.name }}</span>
            </el-tooltip>
          </div>
          <span class="number">{{ item.count | formatBigNumber }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import mixin from './mixin'
import _ from 'lodash'

function generateChartSeries (data, color) {
  return {
    type: 'bar',
    data,
    stack: 'a',
    coordinateSystem: 'polar',
    roundCap: true,
    itemStyle: {
      color: color || '#ec7a3b'
    }
  }
}
const CHART_OPTION = {
  tooltip: {
    show: false,
    formatter: 'The report is in generating...'
  },
  dataset: {
    source: []
  },
  grid: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  angleAxis: {
    splitLine: {
      show: false
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      show: false
    },
    axisTick: {
      show: false
    }
  },
  radiusAxis: {
    type: 'category',
    splitLine: {
      show: false
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      show: false
    },
    axisTick: {
      show: false
    }
  },
  polar: {
    radius: ['30%', '80%']
  },
  series: [],
  legend: {}
}

export default {
  mixins: [mixin],
  props: {
    title: {
      type: String,
      required: true
    },
    number: {
      type: Number,
      required: true
    },
    dataList: {
      type: Array,
      required: false
    },
    groupName: {
      type: String,
      default: ''
    },
    groupID: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.hrDashboard',
      chartInstance: null,
      currentUser: {}
    }
  },
  computed: {
    chartList () {
      const chartList = [
        {
          name: 'Hot',
          count: 0,
          color: '#ec7a3b'
        },
        {
          name: 'Active',
          count: 0,
          color: '#f1d45f'
        },
        {
          name: 'Warm',
          count: 0,
          color: '#98cd95'
        }
      ]
      if (this.hasLeadsPermission) {
        chartList.push({
          name: 'Potential',
          count: 0,
          color: '#b0dde2'
        })
      }
      chartList.push({
        name: 'Cold',
        count: 0,
        color: '#6dadc8'
      })
      return chartList
    },
    emptyCount () {
      return _.times(this.chartList.length, _.constant(0))
    }
  },
  created () {

  },
  destroyed () {
    if (this.chartInstance) {
      echarts.dispose(this.chartInstance)
    }
  },
  methods: {
    handleCommand (command) {
      this.$emit('dataChangeEvent', command)
    },
    initTalentPoolChart () {
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.chart)
        this.chartInstance.setOption(CHART_OPTION)
      }
      const countMap = {}
      _.forEach(this.dataList, item => {
        countMap[item.name] = item.count
      })
      const option = { series: null, angleAxis: { max: this.number } }
      const chartList = this.chartList
      option.series = _.map(chartList, (item, index) => {
        const name = item.name
        const count = countMap[name] || 0
        const data = [name, ...this.emptyCount]
        item.count = count
        data[index + 1] = count
        return generateChartSeries(data, item.color)
      })
      this.chartInstance.setOption(option)
      this.initChartClickEvent(this.chartInstance)
    },
    initChartClickEvent (myChart) {
      const activenessMapping = {
        '#ec7a3b': 'Hot',
        '#f1d45f': 'Active',
        '#98cd95': 'Warm',
        '#b0dde2': 'Potential',
        '#6dadc8': 'Cold'
      }
      myChart.on('click', (params) => {
        const activeness = activenessMapping[params.color] || 'Cold'
        this.goToTalentPool(activeness)
      })
    },
    goToTalentPool (activeness) {
      if (activeness === 'Potential') {
        this.$router.push({ path: `/v-admin/talent-pool/${this.groupID}/potential/list` })
        return
      }
      this.$router.push({ path: `/v-admin/talent-pool/${this.groupID}`, query: { activenessType: activeness } })
    }
  },
  watch: {
    dataList () {
      this.initTalentPoolChart()
    }
  }
}
</script>

<style lang="scss" scoped>
.talentPool-status-container {
  &.single {
    .chart {
      justify-content: center;
      .chart-left {
        width: 200px;
        height: 200px;
      }
      .chart-number {
        margin-left: 20px;
      }
      .chart-list {
        margin-left: 20px;
      }
    }
  }
  .title {
    display: flex;
    justify-content: space-between;
    .title-left {
      color: #606266;
      padding: 0 15px;
      border-radius: 20px;
      line-height: 22px;
      cursor: pointer;
    }
    .title-group-name{
      font-size: 14px;
      font-weight: bold;
      max-width: 220px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .talentPool-open-eye {
      top: 5px;
      right: 8px;
      margin-right: 10px;
      color: #2585b5;
      cursor: pointer;
    }
    .to-detail {
      color: #0096cc;

      > i {
        margin-left: 10px;
      }
    }
  }
  .chart {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 200px;
    .chart-left {
      width: 150px;
      height: 150px;
    }
    .chart-number {
      font-size: 22px;
      height: 30px;
      line-height: 30px;
      color: #303133;
      font-weight: bold;
    }
    .chart-list {
      .chart-li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 160px;
        font-weight: 400;
        margin: 15px 0;
        .color {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 8px;
          margin: 2px 10px 0 20px;
        }
        .name {
          height: 20px;
          font-size: 14px;
          line-height: 20px;
          cursor: pointer;
          color: #606266;
        }
        .number {
          width: 20px;
          margin-right: 20px;
          height: 20px;
          font-size: 14px;
          line-height: 20px;
          color: #909399;
        }
      }
    }
  }
}
</style>
